import { FC, useCallback, useMemo } from "react";
import { Box, Chip, Typography } from "@mui/material";

import ConfigCards from "../../components/ConfigListShell/ConfigCards";
import ConfigListStates, {
	type ListState,
} from "../../components/ConfigListShell/ConfigListStates";
import ConfigListToolbar from "../../components/ConfigListShell/ConfigListToolbar";
import ConfigTable, {
	type ConfigColumn,
} from "../../components/ConfigListShell/ConfigTable";
import {
	type ViewMode,
	useConfigList,
} from "../../components/ConfigListShell/useConfigList";
import { AnalyteStatus, IAnalyte } from "../../domain/analyte.interface";
import { CupResponse } from "../../domain/cup.interface";
import ListFooter from "./ListFooter";
import {
	analyteSortValue,
	analyteTypeCategories,
	analytesStateCopy,
	buildUsageMap,
	searchableAnalyteFields,
	typeFilterOptions,
} from "./analytesList";

/**
 * SIMS-8783 — the Analytes tab.
 *
 * Owns its own `useConfigList`, so switching tabs unmounts it and clears its search — which is what
 * the design does (markup 1239). `view` is NOT owned here: it is lifted to the container because
 * the design shares one view across both tabs.
 *
 * "In cups" is derived from the cups collection, because `GET /analytes` returns no usage figure.
 * It is a COUNT and never names the cups; naming them is SIMS-8814 AC3, in the analyte editor.
 *
 * Because it is derived, it depends on a SECOND query. When that query has not settled or has
 * failed, `cups` is an empty array and every analyte would read "0 cups" — an absence of data
 * rendered as a fact, and a dangerous one: zero is the signal that an analyte is safe to rename or
 * delete (SIMS-8853). `cupsUnavailable` renders an em dash instead.
 */
export interface AnalytesPanelProps {
	analytes: IAnalyte[];
	cups: CupResponse[];
	/** The cups query has not settled, or failed — "In cups" cannot be trusted. */
	cupsUnavailable: boolean;
	/** SIMS-8814 — open an analyte in the editor. */
	onEdit: (analyte: IAnalyte) => void;
	isLoading: boolean;
	isError: boolean;
	view: ViewMode;
}

const AnalytesPanel: FC<AnalytesPanelProps> = ({
	analytes,
	cups,
	cupsUnavailable,
	onEdit,
	isLoading,
	isError,
	view,
}) => {
	// One pass over the cups, reused by the column and the sort comparator.
	const usage = useMemo(() => buildUsageMap(analytes, cups), [analytes, cups]);
	const sortValue = useCallback(
		(row: IAnalyte, key: string) => analyteSortValue(row, key, cups, usage),
		[cups, usage],
	);

	const list = useConfigList<IAnalyte>({
		rows: analytes,
		searchableFields: searchableAnalyteFields,
		categoriesOf: analyteTypeCategories,
		sortValue,
		defaultSortKey: "shortname",
	});

	const types = useMemo(() => typeFilterOptions(analytes), [analytes]);

	const listState: ListState = isLoading
		? "loading"
		: isError
			? "load-failed"
			: analytes.length === 0
				? "empty"
				: list.total === 0
					? "no-results"
					: "ready";

	const columns: ConfigColumn<IAnalyte>[] = [
		{
			key: "shortname",
			header: "Shortname",
			width: "120px",
			sortable: true,
			render: (a) => (
				<Chip
					size="small"
					label={a.shortname}
					sx={{ fontFamily: "monospace", fontSize: "0.75rem", fontWeight: 600 }}
				/>
			),
		},
		{
			key: "scientificName",
			header: "Scientific name",
			width: "1.4fr",
			sortable: true,
			render: (a) => (
				<Typography sx={{ fontSize: "0.875rem", fontWeight: 600 }} noWrap>
					{a.scientificName}
				</Typography>
			),
		},
		{
			key: "display",
			header: "Display",
			width: "1.4fr",
			render: (a) => (
				<Typography
					sx={{ fontSize: "0.8125rem", color: "text.secondary" }}
					noWrap
				>
					{a.scientificDisplay || a.shortnameDisplay}
				</Typography>
			),
		},
		{
			key: "type",
			header: "Type",
			width: "180px",
			sortable: true,
			render: (a) => <Chip size="small" label={String(a.type ?? "")} />,
		},
		{
			key: "status",
			header: "Status",
			width: "120px",
			sortable: true,
			render: (a) => (
				// Text plus colour, never colour alone (NFR-5).
				<Chip
					size="small"
					label={a.status === AnalyteStatus.ACTIVE ? "Active" : "Inactive"}
					color={a.status === AnalyteStatus.ACTIVE ? "success" : "default"}
				/>
			),
		},
		{
			key: "inCups",
			header: "In cups",
			width: "120px",
			sortable: true,
			render: (a) => (
				<Typography
					sx={{ fontSize: "0.8125rem", color: "text.secondary" }}
					data-testid="analyte-in-cups"
					title={
						cupsUnavailable
							? "The cup catalogue could not be loaded"
							: undefined
					}
				>
					{cupsUnavailable ? "—" : (usage.get(a._id) ?? 0)}
				</Typography>
			),
		},
	];

	const resultLabel = list.isNarrowed
		? `Showing ${list.total} of ${list.grandTotal}`
		: `${list.grandTotal} analytes`;

	return (
		<>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					gap: 1.5,
					flexWrap: "wrap",
					py: 2,
				}}
			>
				<ConfigListToolbar
					query={list.query}
					onQueryChange={list.setQuery}
					searchPlaceholder="Search by shortname or scientific name"
					status={list.status}
					onStatusChange={list.setStatus}
					statusOptions={[]}
					categoryLabel="Type"
					category={list.category}
					onCategoryChange={list.setCategory}
					// AC4 — derived from the data, the same way the cups tab derives vendors (Q2).
					categoryOptions={types.map((t) => ({ value: t, label: t }))}
				/>
				<Box sx={{ flex: 1 }} />
				<Typography
					sx={{ fontSize: "0.8125rem", color: "text.secondary" }}
					data-testid="analytes-result-count"
				>
					{resultLabel}
				</Typography>
			</Box>

			{listState !== "ready" ? (
				<ConfigListStates
					state={listState}
					view={view}
					copy={analytesStateCopy}
					query={list.query}
					onClearSearch={() => list.setQuery("")}
					onClearAllFilters={() => {
						list.setQuery("");
						list.setCategory("all");
					}}
				/>
			) : view === "table" ? (
				<ConfigTable<IAnalyte>
					rows={list.visible}
					columns={columns}
					rowKey={(a) => a._id}
					onRowClick={onEdit}
					sortKey={list.sortKey}
					sortDir={list.sortDir}
					onSort={list.toggleSort}
					ariaLabel="Analytes"
				/>
			) : (
				<ConfigCards<IAnalyte>
					rows={list.visible}
					rowKey={(a) => a._id}
					onRowClick={onEdit}
					title={(a) => a.scientificName}
					subtitle={(a) => a.shortname}
					stats={(a) => [
						{ label: "Type", value: String(a.type ?? "") },
						{
							label: "In cups",
							value: cupsUnavailable ? "—" : (usage.get(a._id) ?? 0),
						},
					]}
				/>
			)}

			{listState === "ready" && (
				<ListFooter
					page={list.page}
					pageCount={list.pageCount}
					onPageChange={list.setPage}
					pageSize={list.pageSize}
					onPageSizeChange={list.setPageSize}
					pageSizeOptions={list.pageSizeOptions}
					rangeStart={list.rangeStart}
					rangeEnd={list.rangeEnd}
					total={list.total}
				/>
			)}
		</>
	);
};

export default AnalytesPanel;
